import React from 'react';
import TripDayHotelRoomsModal from '../TripDayHotelRoomsModal';
import TripDayHotelSuggestedModal from '../TripDayHotelSuggestedModal';
import TripHotelCard from '../TripHotelCard';
import type { TripDayHotelProps } from './TripDayHotel';

// Split out of TripDayHotelCardModals (US-062) to stay under the package's
// max-lines-per-function cap. Builds the props for the non-included hotel's
// own room-selection modal (rendered through a slot, still a large, per-app
// component) — the update-on-save logic is the only real behaviour here.
function buildRoomSelectionModalProps({
  dayIndex,
  hotel,
  roomsData,
  roomsComposition,
  dayRoomSelection,
  roomStartDate,
  roomEndDate,
  showRoomSelectModal,
  setShowRoomSelectModal,
  updateHotelRoomSelection,
}: {
  dayIndex: number;
  hotel: any;
  roomsData: any;
  roomsComposition: any[];
  dayRoomSelection: any;
  roomStartDate: any;
  roomEndDate: any;
  showRoomSelectModal: boolean;
  setShowRoomSelectModal: (value: boolean) => void;
  updateHotelRoomSelection: (
    hotelId: string,
    selection: any,
    dayIndex: number,
  ) => void;
}): Parameters<TripDayHotelProps['renderRoomSelectionModal']>[0] {
  return {
    isOpen: showRoomSelectModal,
    onRequestClose: () => setShowRoomSelectModal(false),
    onSave: (rooms: any[]) => {
      // Update hotel with selected rooms (multiple rooms support)
      if (rooms.length > 0) {
        // Join all room keys with commas for multi-room bookings
        const combinedRoomKey = rooms.map((r) => r.roomKey).join(',');

        updateHotelRoomSelection(
          hotel.id,
          {
            roomKey: combinedRoomKey,
            composition: rooms[0], // Keep first room for compatibility
            allRooms: rooms, // Store all selected rooms
            availabilityData: roomsData?.possibilities,
            expiresAt: roomsData?.expiresAt,
            requestId: roomsData?.requestId, // Include requestId for booking policies
          },
          dayIndex,
        );
      }
    },
    hotelData: { possibilities: roomsData?.possibilities || [] },
    totalRoomNumber: roomsComposition.length,
    initialSelectedRooms:
      dayRoomSelection?.allRooms ??
      (dayRoomSelection?.roomData ? [dayRoomSelection.roomData] : []),
    hotelName: hotel?.name ?? hotel?.displayName,
    filters: {
      rooms: roomsComposition,
      // The stay the modal states extra room-nights against (KD-3496).
      startAt: roomStartDate,
      endAt: roomEndDate,
    },
  };
}

// The three modals a loaded hotel day can open: the multi-room composition
// editor, the included-hotel "explore alternatives" picker, and (through a
// slot — still a large, per-app component) the non-included hotel's own
// room-selection modal. Split out of TripDayHotelCard (US-061) to stay under
// the package's max-lines-per-function cap.
export default function TripDayHotelCardModals({
  dayIndex,
  date,
  hotel,
  isFirstInGroup,
  isIncludedHotel,
  roomsComposition,
  displayedRooms,
  roomsData,
  dayRoomSelection,
  roomStartDate,
  roomEndDate,
  suggestedHotelIds,
  tripFilters,
  locale,
  tripHotelCardProps,
  showRoomsModal,
  setShowRoomsModal,
  showModal,
  setShowModal,
  showRoomSelectModal,
  setShowRoomSelectModal,
  onSaveRoomsComposition,
  updateHotelRoomSelection,
  renderRoomSelectionModal,
  t,
}: {
  dayIndex: number;
  date: any;
  hotel: any;
  isFirstInGroup: boolean;
  isIncludedHotel: any;
  roomsComposition: any[];
  displayedRooms: any[];
  roomsData: any;
  dayRoomSelection: any;
  roomStartDate: any;
  roomEndDate: any;
  suggestedHotelIds: any[];
  tripFilters: any;
  locale?: string;
  tripHotelCardProps?: any;
  showRoomsModal: boolean;
  setShowRoomsModal: (value: boolean) => void;
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  showRoomSelectModal: boolean;
  setShowRoomSelectModal: (value: boolean) => void;
  onSaveRoomsComposition: (rooms: any[]) => void;
  updateHotelRoomSelection: (
    hotelId: string,
    selection: any,
    dayIndex: number,
  ) => void;
  renderRoomSelectionModal: TripDayHotelProps['renderRoomSelectionModal'];
  t: (key: string, options?: Record<string, any>) => string;
}) {
  const splitT = (key: string, options?: Record<string, any>) =>
    t(`compose.stayRooms.${key}`, options);

  return (
    <>
      {isFirstInGroup && !isIncludedHotel && (
        <TripDayHotelRoomsModal
          open={showRoomsModal}
          setOpen={setShowRoomsModal}
          title={splitT('title', {
            hotel: hotel?.name ?? hotel?.displayName,
          })}
          description={splitT('description')}
          emptyRoomMessage={splitT('emptyRoom')}
          overTripMessage={splitT('overTrip', {
            adults: tripFilters?.composition?.adults ?? 0,
            children: tripFilters?.composition?.childrenBirthdates?.length ?? 0,
          })}
          resetLabel={splitT('reset')}
          cancelLabel={t('misc:cancel')}
          saveLabel={t('misc:save')}
          rooms={displayedRooms}
          tripComposition={tripFilters?.composition}
          onSave={onSaveRoomsComposition}
          t={splitT}
        />
      )}

      {isIncludedHotel && (
        <TripDayHotelSuggestedModal
          open={showModal}
          close={() => setShowModal(false)}
          hotelIds={suggestedHotelIds}
          isIncluded={true}
          date={date}
          title={t('compose.exploreIncludedHotels')}
          description={t('compose.exploreIncludedHotelsDescription')}
          emptyLabel={t('compose.noIncludedHotels')}
          renderHotelCard={(hotel: any) => (
            <TripHotelCard
              key={hotel.id}
              hotel={hotel}
              addHotel={() => {}}
              hotelWithPrice={hotel}
              isForTrip={true}
              locale={locale}
              {...tripHotelCardProps}
            />
          )}
        />
      )}

      {!isIncludedHotel &&
        roomsData?.possibilities &&
        renderRoomSelectionModal(
          buildRoomSelectionModalProps({
            dayIndex,
            hotel,
            roomsData,
            roomsComposition,
            dayRoomSelection,
            roomStartDate,
            roomEndDate,
            showRoomSelectModal,
            setShowRoomSelectModal,
            updateHotelRoomSelection,
          }),
        )}
    </>
  );
}
